import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { disruptionApi } from "@/api";
import {
  AlertTriangle, Zap, CheckCircle, XCircle, RefreshCw, Plus,
  ChevronRight, Clock, TrendingUp, Shield, DollarSign, Network,
  Users, Plane, CloudLightning, Wrench, BookOpen, X, Activity,
  ArrowRight, Star, Flag, RotateCcw, Calendar, ExternalLink, UserCheck, UserX,
  ShieldCheck, ShieldAlert, ShieldOff, BadgeCheck, BadgeAlert, Download, FileText, FileSpreadsheet, ChevronDown,
} from "lucide-react";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const SEV_CONFIG = {
  critical: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", badge: "bg-red-500/20 text-red-400", dot: "bg-red-500" },
  high:     { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30", badge: "bg-orange-500/20 text-orange-400", dot: "bg-orange-500" },
  medium:   { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", badge: "bg-amber-500/20 text-amber-400", dot: "bg-amber-500" },
  low:      { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", badge: "bg-blue-500/20 text-blue-400", dot: "bg-blue-500" },
};

const STATUS_CONFIG = {
  active:    { label: "Active", color: "text-red-400", bg: "bg-red-500/15" },
  analyzing: { label: "Analyzed", color: "text-amber-400", bg: "bg-amber-500/15" },
  resolving: { label: "Resolving", color: "text-blue-400", bg: "bg-blue-500/15" },
  resolved:  { label: "Resolved", color: "text-green-400", bg: "bg-green-500/15" },
};

const TYPE_ICON = {
  crew_sick_call: Users, crew_no_show: Users, crew_fatigue_declaration: Users,
  crew_medical_grounding: Users, personal_emergency: Users,
  aircraft_aog: Wrench, maintenance_overrun: Wrench, fleet_grounding: Wrench,
  weather_fog: CloudLightning, weather_storm: CloudLightning, airport_closure: CloudLightning, weather_diversion: CloudLightning,
  cascading_delay: Network, reserve_depletion: Network,
  fdp_limit_breach: Shield, rest_violation: Shield, cumulative_breach: Shield,
  license_expiry: BookOpen, type_rating_expired: BookOpen, recency_violation: BookOpen,
  simulator_breakdown: BookOpen, instructor_unavailable: BookOpen,
  crew_connection_failure: Plane, deadhead_missed: Plane,
  strike_warning: Flag, mass_sick_leave: Flag,
};

const DISRUPTION_TYPES = [
  { value: "crew_sick_call", label: "Crew Sick Call", group: "Crew" },
  { value: "crew_no_show", label: "Crew No-Show", group: "Crew" },
  { value: "crew_fatigue_declaration", label: "Fatigue Declaration", group: "Crew" },
  { value: "crew_medical_grounding", label: "Medical Grounding", group: "Crew" },
  { value: "personal_emergency", label: "Personal Emergency", group: "Crew" },
  { value: "license_expiry", label: "License Expiry", group: "Qualification" },
  { value: "type_rating_expired", label: "Type Rating Expired", group: "Qualification" },
  { value: "recency_violation", label: "Recency Violation", group: "Qualification" },
  { value: "aircraft_aog", label: "Aircraft AOG", group: "Aircraft" },
  { value: "maintenance_overrun", label: "Maintenance Overrun", group: "Aircraft" },
  { value: "fleet_grounding", label: "Fleet Grounding", group: "Aircraft" },
  { value: "weather_fog", label: "Fog / Low Visibility", group: "Weather" },
  { value: "weather_storm", label: "Storm / Severe Weather", group: "Weather" },
  { value: "airport_closure", label: "Airport Closure", group: "Weather" },
  { value: "weather_diversion", label: "Weather Diversion", group: "Weather" },
  { value: "departure_delay", label: "Departure Delay", group: "Flight" },
  { value: "flight_cancellation", label: "Flight Cancellation", group: "Flight" },
  { value: "fdp_limit_breach", label: "FDP Limit Breach", group: "Compliance" },
  { value: "rest_violation", label: "Rest Violation", group: "Compliance" },
  { value: "cumulative_breach", label: "Cumulative Hours Breach", group: "Compliance" },
  { value: "crew_connection_failure", label: "Crew Connection Failure", group: "Operations" },
  { value: "cascading_delay", label: "Cascading Network Delay", group: "Operations" },
  { value: "hotel_unavailable", label: "Hotel Unavailable", group: "Logistics" },
  { value: "simulator_breakdown", label: "Simulator Breakdown", group: "Training" },
  { value: "instructor_unavailable", label: "Instructor Unavailable", group: "Training" },
  { value: "strike_warning", label: "Strike / Industrial Action", group: "Industrial" },
  { value: "mass_sick_leave", label: "Mass Sick Leave", group: "Industrial" },
  { value: "system_outage", label: "System / Data Failure", group: "System" },
];

function ScoreBar({ label, value, color = "bg-blue-500" }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{value}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ImpactCard({ icon: Icon, title, value, sub, color }) {
  return (
    <div className="bg-background/50 border border-border/60 rounded-sm p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={12} className={color} />
        <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">{title}</span>
      </div>
      <div className={`text-sm font-bold font-mono ${color}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function RecoveryOptionCard({ option, idx, onAccept, onOverride, resolvedOptionId }) {
  const isRecommended = option.recommended;
  const isAccepted = resolvedOptionId === option.id;
  const scoreColors = {
    compliance: "bg-green-500",
    fatigue: "bg-blue-500",
    cost: "bg-purple-500",
    delay: "bg-amber-500",
    fairness: "bg-pink-500",
  };

  return (
    <div className={`border rounded-sm p-4 transition-all ${
      isAccepted ? "border-green-500/50 bg-green-500/5" :
      isRecommended ? "border-primary/40 bg-primary/5" : "border-border bg-card/50"
    }`} data-testid={`recovery-option-${idx}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isRecommended && !isAccepted && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-sm">
                <Star size={9} /> AI Recommended
              </span>
            )}
            {isAccepted && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-sm">
                <CheckCircle size={9} /> Accepted
              </span>
            )}
            <span className="px-1.5 py-0.5 bg-secondary text-muted-foreground text-xs rounded-sm font-mono">
              {option.type.replace(/_/g, " ").toUpperCase()}
            </span>
          </div>
          <div className="font-bold text-sm text-foreground mt-1">{option.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-black font-mono text-foreground">{option.overall_score}</div>
          <div className="text-xs text-muted-foreground">Overall</div>
        </div>
      </div>

      {/* Score Bars */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
        <ScoreBar label="Compliance" value={option.scores?.compliance || 0} color={scoreColors.compliance} />
        <ScoreBar label="Fatigue" value={option.scores?.fatigue || 0} color={scoreColors.fatigue} />
        <ScoreBar label="Cost" value={option.scores?.cost || 0} color={scoreColors.cost} />
        <ScoreBar label="Delay" value={option.scores?.delay || 0} color={scoreColors.delay} />
        <ScoreBar label="Fairness" value={option.scores?.fairness || 0} color={scoreColors.fairness} />
      </div>

      {/* AI Narrative */}
      {option.ai_narrative && (
        <div className="flex items-start gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-sm mb-3">
          <Zap size={11} className="text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-foreground/90 italic">{option.ai_narrative}</p>
        </div>
      )}

      {/* Risk flags */}
      {option.risk_flags?.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3">
          {option.risk_flags.map((f, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs rounded-sm">
              {f}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      {!isAccepted && (
        <div className="flex gap-2 pt-2 border-t border-border/40">
          <button onClick={() => onAccept(option)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid={`accept-option-${idx}`}>
            <CheckCircle size={11} /> Accept Option
          </button>
          <button onClick={() => onOverride(option)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground text-xs font-semibold rounded-sm hover:border-amber-500/50 hover:text-amber-400 transition-colors"
            data-testid={`override-option-${idx}`}>
            <RotateCcw size={11} /> Override
          </button>
        </div>
      )}
    </div>
  );
}

function NewDisruptionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: "crew_sick_call", severity: "high", title: "", description: "",
    base: "DEL", affected_crew_text: "", affected_flights_text: "", affected_aircraft_text: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type, severity: form.severity, title: form.title,
        description: form.description, base: form.base,
        affected_crew: form.affected_crew_text ? form.affected_crew_text.split(",").map(s => ({ name: s.trim() })) : [],
        affected_flights: form.affected_flights_text ? form.affected_flights_text.split(",").map(s => s.trim()).filter(Boolean) : [],
        affected_aircraft: form.affected_aircraft_text ? form.affected_aircraft_text.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      const res = await disruptionApi.createEvent(payload);
      onCreated(res.data);
      onClose();
    } catch (err) {
      alert("Failed to create disruption");
    } finally {
      setSaving(false);
    }
  };

  const grouped = DISRUPTION_TYPES.reduce((acc, t) => {
    if (!acc[t.group]) acc[t.group] = [];
    acc[t.group].push(t);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="new-disruption-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Ops Control</div>
            <h2 className="font-bold text-foreground">Report New Disruption</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                {Object.entries(grouped).map(([group, types]) => (
                  <optgroup key={group} label={group}>
                    {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Severity</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                {["critical","high","medium","low"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
              placeholder="e.g. Capt. Singh — Sick Call (QP-101)"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required rows={3}
              placeholder="Full operational description of the disruption..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Base</label>
              <select value={form.base} onChange={e => setForm(f => ({ ...f, base: e.target.value }))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                {["DEL","BOM","BLR","HYD","MAA","CCU","AMD","PNQ"].map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Affected Flights</label>
              <input value={form.affected_flights_text} onChange={e => setForm(f => ({ ...f, affected_flights_text: e.target.value }))}
                placeholder="QP-101, QP-203 (comma sep)"
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Affected Crew (comma-separated names)</label>
            <input value={form.affected_crew_text} onChange={e => setForm(f => ({ ...f, affected_crew_text: e.target.value }))}
              placeholder="Capt. Vikram Singh, FO Anita Rao"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
              data-testid="create-disruption-submit">
              {saving ? "Creating..." : "Create Disruption"}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-border text-muted-foreground text-xs font-semibold rounded-sm hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OverrideModal({ option, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [resolvedBy, setResolvedBy] = useState("Operations Control");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="override-modal">
      <div className="bg-card border border-amber-500/30 rounded-sm w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-xs tracking-widest uppercase text-amber-400 font-bold">Manual Override</div>
            <h2 className="font-bold text-foreground text-sm">{option ? `Override: ${option.title}` : "Custom Override"}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Override Reason (required)</label>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Commercial necessity, DGCA dispensation granted"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500" />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Custom Action Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              placeholder="Describe the override action taken..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
          </div>
          <div>
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1 block">Resolved By</label>
            <input value={resolvedBy} onChange={e => setResolvedBy(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => onConfirm({ reason, description, resolvedBy })} disabled={!reason}
              className="flex-1 px-4 py-2 bg-amber-500 text-black text-xs font-bold rounded-sm hover:bg-amber-400 disabled:opacity-50 transition-colors"
              data-testid="confirm-override-btn">
              Confirm Override
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-border text-muted-foreground text-xs font-semibold rounded-sm hover:text-foreground transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mini Gantt for Roster Impact ─────────────────────────────────────────
// Derive current roster month from IST for the mini-Gantt
const _ist = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000));
const _istY = _ist.getUTCFullYear(), _istM = _ist.getUTCMonth() + 1;
const ROSTER_DAYS = Array.from({ length: 30 }, (_, i) =>
  `${_istY}-${String(_istM).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`
);
const ROSTER_MONTH_LABEL = new Date(_istY, _istM - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
const GANTT_COLORS = {
  flight: { bg: "#2563eb", label: "FLT" },
  reserve: { bg: "#d97706", label: "RSV" },
  training: { bg: "#7c3aed", label: "TRN" },
  leave: { bg: "#475569", label: "LVE" },
  standby: { bg: "#ca8a04", label: "SBY" },
  disruption_cover: { bg: "#16a34a", label: "DIS" },
  disrupted: { bg: "#991b1b", label: "DIS!" },
  cancelled: { bg: "#3f3f46", label: "CNX" },
};

function MiniGanttRow({ crew, assignments, isRecovery = false }) {
  const aMap = {};
  for (const a of assignments) {
    const start = new Date(a.start_date);
    const end = new Date(a.end_date);
    for (const d of ROSTER_DAYS) {
      const cur = new Date(d);
      if (cur >= start && cur <= end) {
        const type = a.status === "cancelled" ? "cancelled"
          : a.assignment_type === "disruption_cover" ? "disruption_cover"
          : a.status === "disrupted" ? "disrupted"
          : a.assignment_type;
        aMap[d] = { type, disruption_id: a.disruption_id };
      }
    }
  }

  return (
    <div className="flex items-center gap-0 min-w-0">
      <div className={`w-32 shrink-0 pr-2 text-xs font-semibold truncate ${isRecovery ? "text-green-400" : "text-foreground"}`}>
        {isRecovery && <span className="text-green-500 mr-1">+</span>}
        {crew.name?.split(" ").slice(-1)[0]} · {crew.rank}
      </div>
      <div className="flex gap-px flex-1">
        {ROSTER_DAYS.map(d => {
          const cell = aMap[d];
          const cfg = cell ? (GANTT_COLORS[cell.type] || GANTT_COLORS.flight) : null;
          const isDisrupted = cell?.disruption_id;
          return (
            <div key={d} style={{
              width: 14, height: 14, borderRadius: 2, flexShrink: 0,
              background: cfg ? cfg.bg : "transparent",
              border: isDisrupted && cell.type !== "disruption_cover" ? "1px solid #f97316" : "none",
              opacity: cfg ? 1 : 0.15,
            }} title={`${d.slice(5)}: ${cfg?.label || "OFF"}`} />
          );
        })}
      </div>
    </div>
  );
}

function RosterImpactPanel({ disruption, onNavigate }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!disruption?.id) return;
    setLoading(true);
    disruptionApi.getRosterImpact(disruption.id)
      .then(r => setImpact(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [disruption?.id, disruption?.status]);

  if (loading) return (
    <div className="py-4 text-xs text-muted-foreground animate-pulse flex items-center gap-2">
      <RefreshCw size={11} className="animate-spin" /> Loading roster data...
    </div>
  );
  if (!impact) return null;

  const { affected_crew_schedules, available_reserves, recovery_assignments, is_resolved } = impact;

  // Legend day labels (1-30 Apr)
  const dayLabels = ["1","","","","5","","","","","10","","","","","15","","","","","20","","","","","25","","","","","30"];

  return (
    <div data-testid="roster-impact-panel">
      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
        <Calendar size={11} /> Roster Impact · {ROSTER_MONTH_LABEL}
        <button onClick={onNavigate}
          className="ml-auto flex items-center gap-1 text-primary hover:text-primary/80 transition-colors text-xs font-normal"
          data-testid="open-roster-planner-btn">
          <ExternalLink size={10} /> Full Roster
        </button>
      </div>

      <div className="bg-background/50 border border-border/60 rounded-sm p-3 space-y-2">
        {/* Day header */}
        <div className="flex items-center gap-0">
          <div className="w-32 shrink-0 text-xs text-muted-foreground/40">CREW</div>
          <div className="flex gap-px flex-1">
            {dayLabels.map((l, i) => (
              <div key={i} style={{ width: 14, fontSize: 8, color: "#475569", textAlign: "center", flexShrink: 0 }}>{l}</div>
            ))}
          </div>
        </div>

        {/* Affected crew rows */}
        {affected_crew_schedules.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">No crew with roster data found for this disruption.</div>
        ) : (
          affected_crew_schedules.map(({ crew, assignments }) => (
            <MiniGanttRow key={crew.id} crew={crew} assignments={assignments} />
          ))
        )}

        {/* Recovery assignments */}
        {is_resolved && recovery_assignments.map((a, i) => {
          const fakeCrewId = a.crew_member_id;
          const reserve = available_reserves.find(r => r.id === fakeCrewId);
          if (!reserve) return null;
          return <MiniGanttRow key={i} crew={reserve} assignments={[a]} isRecovery />;
        })}

        {/* Legend */}
        <div className="flex gap-3 pt-2 border-t border-border/40 flex-wrap">
          {[["FLT","#2563eb"],["RSV","#d97706"],["DIS","#16a34a"],["DIS!","#991b1b"],["CNX","#3f3f46"]].map(([label, color]) => (
            <div key={label} className="flex items-center gap-1 text-xs text-muted-foreground">
              <span style={{ display:"inline-block", width:10, height:10, borderRadius:2, background:color }} />
              {label}
            </div>
          ))}
          <div className="flex items-center gap-1 text-xs text-orange-400">
            <span style={{ display:"inline-block", width:10, height:10, borderRadius:2, border:"1px solid #f97316" }} />
            Disruption flagged
          </div>
        </div>
      </div>

      {/* Available Reserves */}
      {!is_resolved && available_reserves.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <UserCheck size={11} className="text-green-400" /> Available Reserves at {disruption.base}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {available_reserves.filter(r => r.is_free).slice(0, 4).map(r => (
              <div key={r.id} className="flex items-center gap-2 px-3 py-2 bg-green-500/5 border border-green-500/20 rounded-sm text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <div>
                  <div className="font-semibold text-foreground">{r.name}</div>
                  <div className="text-muted-foreground">{r.rank} · {r.base}</div>
                </div>
              </div>
            ))}
            {available_reserves.filter(r => !r.is_free).slice(0, 2).map(r => (
              <div key={r.id} className="flex items-center gap-2 px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-sm text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <div>
                  <div className="font-semibold text-foreground">{r.name}</div>
                  <div className="text-muted-foreground">{r.rank} · Busy</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Post-resolution roster changes */}
      {is_resolved && impact.resolution?.roster_changes?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2">Applied Roster Changes</div>
          <div className="space-y-1">
            {impact.resolution.roster_changes.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-xs px-3 py-1.5 bg-green-500/5 border border-green-500/20 rounded-sm">
                <CheckCircle size={10} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-foreground">{c.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Eligibility Panel ───────────────────────────────────────────────────────
function EligibilityTag({ type, text }) {
  if (type === "block") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500/15 border border-red-500/25 rounded text-red-400 text-xs">
      <ShieldOff size={9} /> {text}
    </span>
  );
  if (type === "warn") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/25 rounded text-amber-400 text-xs">
      <ShieldAlert size={9} /> {text}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500/15 border border-green-500/25 rounded text-green-400 text-xs">
      <ShieldCheck size={9} /> Eligible
    </span>
  );
}

function EligibilityPanel({ disruption }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!disruption?.id || !expanded) return;
    setLoading(true);
    disruptionApi.getEligibleReserves(disruption.id)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [disruption?.id, expanded]);

  const reserves = data?.reserves || [];
  const freeReserves = reserves.filter(r => r.is_free);

  return (
    <div className="border border-border/50 rounded-sm bg-card/30">
      {/* Header — always visible */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-primary/5 transition-colors"
        onClick={() => setExpanded(v => !v)}
        data-testid="eligibility-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <BadgeCheck size={13} className="text-primary" />
          <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
            Qualification Eligibility Check
          </span>
          {data && (
            <span className="flex items-center gap-1 text-xs">
              <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded font-mono font-bold">
                {data.eligible_count} eligible
              </span>
              {data.ineligible_count > 0 && (
                <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded font-mono font-bold">
                  {data.ineligible_count} blocked
                </span>
              )}
              {data.fleet && (
                <span className="px-1.5 py-0.5 bg-secondary text-muted-foreground rounded font-mono">
                  {data.fleet}
                </span>
              )}
            </span>
          )}
        </div>
        <ChevronRight size={13} className={`text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {/* Body — visible when expanded */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 animate-pulse">
              <RefreshCw size={11} className="animate-spin" /> Checking qualifications...
            </div>
          ) : freeReserves.length === 0 ? (
            <div className="text-xs text-muted-foreground py-3 text-center">No available crew found at {disruption.base}.</div>
          ) : (
            <>
              {data?.fleet && (
                <div className="text-xs text-muted-foreground bg-secondary/30 rounded-sm px-3 py-2">
                  Checking eligibility for <span className="font-mono font-bold text-foreground">{data.fleet}</span> at <span className="font-mono font-bold text-foreground">{data.base}</span>. Criteria: type rating, airport clearance, critical training (SEP/CRM/FIRE_EVAC/SECURITY).
                </div>
              )}
              <div className="space-y-2">
                {freeReserves.map(crew => {
                  const blocked = crew.eligibility?.flags || [];
                  const warned  = crew.eligibility?.warnings || [];
                  return (
                    <div
                      key={crew.id}
                      className={`border rounded-sm p-3 transition-all ${
                        crew.eligible
                          ? "border-green-500/25 bg-green-500/5"
                          : "border-red-500/20 bg-red-500/5 opacity-80"
                      }`}
                      data-testid={`eligibility-row-${crew.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-6 h-6 rounded-sm flex items-center justify-center text-xs font-black ${crew.eligible ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                              {crew.eligible ? <ShieldCheck size={11} /> : <ShieldOff size={11} />}
                            </div>
                            <div>
                              <div className="text-xs font-bold text-foreground">{crew.name}</div>
                              <div className="text-xs text-muted-foreground">{crew.rank} · {crew.fleet} · {crew.base}</div>
                            </div>
                          </div>
                          {/* Blockers */}
                          {blocked.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {blocked.map((f, i) => <EligibilityTag key={i} type="block" text={f} />)}
                            </div>
                          )}
                          {/* Warnings */}
                          {warned.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {warned.map((w, i) => <EligibilityTag key={i} type="warn" text={w} />)}
                            </div>
                          )}
                          {/* Clean bill */}
                          {blocked.length === 0 && warned.length === 0 && (
                            <EligibilityTag type="ok" text="" />
                          )}
                        </div>
                        <div className={`shrink-0 text-xs font-bold px-2 py-1 rounded-sm ${crew.eligible ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
                          {crew.eligible ? "ELIGIBLE" : "BLOCKED"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DisruptionExportMenu({ filtered, selected }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function exportListCSV() {
    exportCSV({
      headers: [
        { label: "ID",          key: "id"          },
        { label: "Severity",    key: "severity"    },
        { label: "Status",      key: "status"      },
        { label: "Type",        key: "type_label"  },
        { label: "Title",       key: "title"       },
        { label: "Base",        key: "base"        },
        { label: "Reported At", key: "reported_at" },
        { label: "Affected Flights", key: "flights_str" },
      ],
      rows: filtered.map(e => ({
        ...e,
        flights_str: (e.affected_flights || []).join(", "),
        reported_at: new Date(e.reported_at).toLocaleString("en-IN"),
      })),
      filename: `disruption-events-${todayStr()}.csv`,
    });
    setOpen(false);
  }

  function exportListPDF() {
    exportPDF({
      title: "Disruption Events Report",
      subtitle: `${filtered.length} events · Exported ${todayStr()}`,
      columns: [
        { header: "Severity",    key: "severity",    width: 18 },
        { header: "Status",      key: "status",      width: 18 },
        { header: "Type",        key: "type_label",  width: 32 },
        { header: "Title",       key: "title",       width: 60 },
        { header: "Base",        key: "base",        width: 12 },
        { header: "Reported",    key: "reported_at", width: 32 },
      ],
      rows: filtered.map(e => ({
        ...e,
        severity:    e.severity?.toUpperCase(),
        status:      e.status?.toUpperCase(),
        reported_at: new Date(e.reported_at).toLocaleString("en-IN"),
      })),
      filename: `disruption-events-${todayStr()}.pdf`,
    });
    setOpen(false);
  }

  function exportSelectedPDF() {
    if (!selected) return;
    const opts = selected.recovery_options || [];
    exportPDF({
      title: "Disruption Detail Report",
      subtitle: `${selected.title} · ${selected.base} · ${selected.severity?.toUpperCase()}`,
      columns: [
        { header: "Option",      key: "title",       width: 50 },
        { header: "Type",        key: "type_str",    width: 28 },
        { header: "Score",       key: "overall_score",width: 14 },
        { header: "Compliance",  key: "comp_score",  width: 18 },
        { header: "Fatigue",     key: "fat_score",   width: 16 },
        { header: "Cost",        key: "cost_score",  width: 14 },
        { header: "Recommended", key: "recommended_str", width: 20 },
        { header: "Narrative",   key: "ai_narrative",width: 60 },
      ],
      rows: opts.length > 0 ? opts.map(o => ({
        ...o,
        type_str:         o.type?.replace(/_/g, " ").toUpperCase(),
        comp_score:       o.scores?.compliance ?? "—",
        fat_score:        o.scores?.fatigue ?? "—",
        cost_score:       o.scores?.cost ?? "—",
        recommended_str:  o.recommended ? "YES" : "—",
        ai_narrative:     o.ai_narrative || "—",
      })) : [{ title: "No recovery options generated yet", type_str: "—", overall_score: "—", comp_score: "—", fat_score: "—", cost_score: "—", recommended_str: "—", ai_narrative: "—" }],
      filename: `disruption-detail-${selected.id?.substring(0,8)}-${todayStr()}.pdf`,
    });
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 rounded-sm transition-colors"
        data-testid="disruption-export-btn">
        <Download size={12} /> Export <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-50 py-1 min-w-[230px]">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border mb-1">Download Report</div>
          <button onClick={exportListPDF}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors text-left">
            <FileText size={11} className="text-muted-foreground" /> All Events — PDF
          </button>
          <button onClick={exportListCSV}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors text-left">
            <FileSpreadsheet size={11} className="text-muted-foreground" /> All Events — CSV
          </button>
          {selected && (
            <button onClick={exportSelectedPDF}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors text-left border-t border-border mt-1 pt-2">
              <FileText size={11} className="text-amber-400" /> Selected Disruption — PDF
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function DisruptionConsole() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [filterSev, setFilterSev] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [overrideOption, setOverrideOption] = useState(null);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, stRes] = await Promise.all([
        disruptionApi.getEvents(),
        disruptionApi.getStats(),
      ]);
      setEvents(evRes.data);
      setStats(stRes.data);
      if (selected) {
        const refreshed = evRes.data.find(e => e.id === selected.id);
        if (refreshed) setSelected(refreshed);
      }
    } catch (err) {
      setError("Failed to load disruptions");
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { fetchAll(); }, []);

  const handleAnalyze = async () => {
    if (!selected) return;
    setAnalyzing(true);
    setError(null);
    try {
      const res = await disruptionApi.analyzeEvent(selected.id);
      setSelected(res.data);
      setEvents(prev => prev.map(e => e.id === selected.id ? res.data : e));
    } catch (err) {
      setError("AI analysis failed. Please retry.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAccept = async (option) => {
    if (!selected) return;
    setResolving(true);
    setError(null);
    try {
      const res = await disruptionApi.resolveEvent(selected.id, {
        action_type: "accept",
        option_id: option.id,
        resolved_by: "Operations Control",
      });
      setSelected(res.data);
      setEvents(prev => prev.map(e => e.id === selected.id ? res.data : e));
      await fetchAll();
    } catch (err) {
      setError("Failed to apply resolution");
    } finally {
      setResolving(false);
    }
  };

  const handleOverrideConfirm = async ({ reason, description, resolvedBy }) => {
    if (!selected) return;
    setOverrideOption(null);
    setResolving(true);
    try {
      const res = await disruptionApi.resolveEvent(selected.id, {
        action_type: "override",
        override_reason: reason,
        override_description: description,
        resolved_by: resolvedBy,
      });
      setSelected(res.data);
      setEvents(prev => prev.map(e => e.id === selected.id ? res.data : e));
      await fetchAll();
    } catch (err) {
      setError("Override failed");
    } finally {
      setResolving(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await disruptionApi.deleteEvent(id);
      if (selected?.id === id) setSelected(null);
      setEvents(prev => prev.filter(e => e.id !== id));
      await fetchAll();
    } catch { setError("Failed to dismiss disruption"); }
  };

  const filtered = events.filter(e =>
    (!filterSev || e.severity === filterSev) &&
    (!filterStatus || e.status === filterStatus)
  );

  const resolvedOptionId = selected?.resolution?.action_type === "accept"
    ? selected?.resolution?.selected_option?.id
    : null;

  const aiContext = selected?.recovery_options?.[0];

  return (
    <div className="h-full flex flex-col" data-testid="disruption-console">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-0.5">AI Disruption Engine · Ops Control</div>
          <h1 className="text-xl font-black font-heading text-foreground">Disruption Console</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} className="p-2 border border-border rounded-sm text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <DisruptionExportMenu filtered={filtered} selected={selected} />
          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid="new-disruption-btn">
            <Plus size={13} /> Report Disruption
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-5 gap-px bg-border border-b border-border shrink-0">
          {[
            ["Active", stats.active, "text-foreground"],
            ["Critical", stats.by_severity.critical, "text-red-400"],
            ["High", stats.by_severity.high, "text-orange-400"],
            ["Medium", stats.by_severity.medium, "text-amber-400"],
            ["Resolved", stats.resolved, "text-green-400"],
          ].map(([lbl, val, col]) => (
            <div key={lbl} className="bg-card px-4 py-2.5 text-center">
              <div className={`text-lg font-black font-mono ${col}`}>{val}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">{lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-xs text-red-400 shrink-0">
          <AlertTriangle size={12} /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={12} /></button>
        </div>
      )}

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Disruption List */}
        <div className="w-80 shrink-0 border-r border-border flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border shrink-0 flex gap-2">
            <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
              className="flex-1 bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
              <option value="">All Severity</option>
              {["critical","high","medium","low"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="flex-1 bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
              <option value="">All Status</option>
              {["active","analyzing","resolved"].map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground animate-pulse">Loading disruptions...</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">No disruptions match the filter.</div>
            ) : filtered.map(event => {
              const sev = SEV_CONFIG[event.severity] || SEV_CONFIG.medium;
              const sta = STATUS_CONFIG[event.status] || STATUS_CONFIG.active;
              const TypeIcon = TYPE_ICON[event.type] || AlertTriangle;
              const isSelected = selected?.id === event.id;
              return (
                <div key={event.id}
                  onClick={() => setSelected(event)}
                  className={`p-3 border-b border-border/50 cursor-pointer transition-all hover:bg-primary/5 ${isSelected ? "bg-primary/8 border-l-2 border-l-primary" : ""}`}
                  data-testid={`disruption-item-${event.severity}`}>
                  <div className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${sev.dot} ${event.status !== "resolved" ? "animate-pulse" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded-sm text-xs font-bold ${sev.badge}`}>{event.severity.toUpperCase()}</span>
                        <span className={`px-1.5 py-0.5 rounded-sm text-xs ${sta.bg} ${sta.color}`}>{sta.label}</span>
                      </div>
                      <div className="text-xs font-semibold text-foreground leading-snug">{event.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <TypeIcon size={10} className="text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">{event.type_label}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{event.base}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                        {new Date(event.reported_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <ChevronRight size={12} className="text-muted-foreground shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div className="flex-1 overflow-y-auto">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <Activity size={40} className="text-muted-foreground/30 mb-4" />
              <div className="text-sm font-semibold text-muted-foreground">Select a disruption to view details</div>
              <div className="text-xs text-muted-foreground/60 mt-1">AI-powered impact assessment and recovery options will appear here</div>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Disruption Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {(() => { const sev = SEV_CONFIG[selected.severity] || SEV_CONFIG.medium; return (
                      <span className={`px-2 py-1 rounded-sm text-xs font-black ${sev.badge}`}>{selected.severity.toUpperCase()}</span>
                    );})()}
                    {(() => { const sta = STATUS_CONFIG[selected.status] || STATUS_CONFIG.active; return (
                      <span className={`px-2 py-1 rounded-sm text-xs font-semibold ${sta.bg} ${sta.color}`}>{sta.label}</span>
                    );})()}
                    <span className="px-2 py-1 bg-secondary text-muted-foreground text-xs rounded-sm">{selected.type_label}</span>
                    <span className="px-2 py-1 bg-secondary text-muted-foreground text-xs rounded-sm font-mono">{selected.base}</span>
                  </div>
                  <h2 className="text-lg font-black text-foreground">{selected.title}</h2>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{selected.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock size={10} /> {new Date(selected.reported_at).toLocaleString("en-IN")}</span>
                    {selected.affected_crew?.length > 0 && (
                      <span className="flex items-center gap-1"><Users size={10} /> {selected.affected_crew.map(c => c.name).join(", ")}</span>
                    )}
                    {selected.affected_flights?.length > 0 && (
                      <span className="flex items-center gap-1"><Plane size={10} /> {selected.affected_flights.join(", ")}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => handleDismiss(selected.id)}
                  className="shrink-0 p-2 text-muted-foreground hover:text-red-400 border border-border hover:border-red-500/30 rounded-sm transition-colors"
                  data-testid="dismiss-disruption-btn">
                  <X size={14} />
                </button>
              </div>

              {/* Roster Impact */}
              <div className="border border-border/50 rounded-sm p-4 bg-card/30">
                <RosterImpactPanel disruption={selected} onNavigate={() => navigate("/roster/planner")} />
              </div>

              {/* Qualification Eligibility Check */}
              <EligibilityPanel disruption={selected} />

              {/* Impact Assessment */}
              {selected.impact_assessment && (
                <div>
                  <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
                    <TrendingUp size={11} /> Impact Assessment
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <ImpactCard icon={Shield} title="Legality"
                      value={selected.impact_assessment.legality_impact?.violations?.length > 0 ? `${selected.impact_assessment.legality_impact.violations.length} Violation${selected.impact_assessment.legality_impact.violations.length > 1 ? "s" : ""}` : "Clear"}
                      sub={selected.impact_assessment.legality_impact?.violations?.[0]?.description?.substring(0, 40) || "No violations detected"}
                      color={selected.impact_assessment.legality_impact?.violations?.length > 0 ? "text-red-400" : "text-green-400"} />
                    <ImpactCard icon={Users} title="Fatigue"
                      value={`+${selected.impact_assessment.fatigue_impact?.average_fatigue_increase || 0} pts avg`}
                      sub={`${selected.impact_assessment.fatigue_impact?.affected_crew?.length || 0} crew affected`}
                      color="text-amber-400" />
                    <ImpactCard icon={Clock} title="Delay"
                      value={`${selected.impact_assessment.delay_impact?.total_delay_minutes || 0} min`}
                      sub={`${selected.impact_assessment.delay_impact?.flights_affected || 0} flights · ${selected.impact_assessment.delay_impact?.cascade_risk?.toUpperCase() || "LOW"} cascade`}
                      color="text-orange-400" />
                    <ImpactCard icon={DollarSign} title="Cost Est."
                      value={`₹${((selected.impact_assessment.cost_impact?.estimated_inr || 0) / 100000).toFixed(1)}L`}
                      sub={`~$${((selected.impact_assessment.cost_impact?.estimated_usd || 0) / 1000).toFixed(0)}K USD`}
                      color="text-purple-400" />
                  </div>
                  {/* Legality violations detail */}
                  {selected.impact_assessment.legality_impact?.violations?.length > 0 && (
                    <div className="space-y-1">
                      {selected.impact_assessment.legality_impact.violations.map((v, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/5 border border-red-500/20 rounded-sm text-xs">
                          <AlertTriangle size={10} className="text-red-400 shrink-0" />
                          <span className="text-red-400 font-mono font-bold">{v.rule}</span>
                          <span className="text-muted-foreground">{v.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selected.impact_assessment.network_impact?.description && (
                    <div className="flex items-start gap-2 px-3 py-2 mt-2 bg-secondary/30 border border-border rounded-sm text-xs">
                      <Network size={10} className="text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{selected.impact_assessment.network_impact.description}</span>
                    </div>
                  )}
                </div>
              )}

              {/* AI Analysis / Recovery Options */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold flex items-center gap-2">
                    <Zap size={11} className="text-primary" /> AI Recovery Options
                  </div>
                  {selected.status !== "resolved" && (
                    <button onClick={handleAnalyze} disabled={analyzing}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary text-xs font-bold rounded-sm hover:bg-primary/20 disabled:opacity-50 transition-all"
                      data-testid="analyze-btn">
                      <Zap size={11} className={analyzing ? "animate-pulse" : ""} />
                      {analyzing ? "AI Analyzing..." : selected.recovery_options?.length > 0 ? "Re-analyze" : "Run AI Analysis"}
                    </button>
                  )}
                </div>

                {/* AI Situation Summary */}
                {aiContext?.situation_summary && (
                  <div className="flex items-start gap-2 px-3 py-2 mb-3 bg-primary/5 border border-primary/20 rounded-sm text-xs">
                    <Zap size={11} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <div className="text-primary font-bold mb-0.5">AI Situation Assessment</div>
                      <div className="text-foreground/90">{aiContext.situation_summary}</div>
                      {aiContext.recommendation_reason && (
                        <div className="text-muted-foreground mt-1 italic">{aiContext.recommendation_reason}</div>
                      )}
                    </div>
                  </div>
                )}

                {analyzing && (
                  <div className="flex items-center gap-3 px-4 py-6 bg-card border border-border rounded-sm text-xs text-muted-foreground">
                    <Zap size={14} className="text-primary animate-pulse" />
                    Claude AI is evaluating {selected.recovery_options?.length || "all"} recovery options across 5 dimensions...
                  </div>
                )}

                {!analyzing && selected.recovery_options?.length === 0 && selected.status !== "resolved" && (
                  <div className="flex flex-col items-center py-8 text-center bg-card border border-border rounded-sm">
                    <Zap size={24} className="text-muted-foreground/30 mb-2" />
                    <div className="text-xs text-muted-foreground">Click "Run AI Analysis" to generate ranked recovery options</div>
                  </div>
                )}

                {selected.recovery_options?.length > 0 && (
                  <div className="space-y-3">
                    {selected.recovery_options.map((opt, idx) => (
                      <RecoveryOptionCard key={opt.id} option={opt} idx={idx}
                        onAccept={handleAccept}
                        onOverride={() => setOverrideOption(opt)}
                        resolvedOptionId={resolvedOptionId} />
                    ))}
                  </div>
                )}
              </div>

              {/* Resolution Summary */}
              {selected.resolution && (
                <div className={`border rounded-sm p-4 ${selected.resolution.action_type === "accept" ? "border-green-500/30 bg-green-500/5" : "border-amber-500/30 bg-amber-500/5"}`}
                  data-testid="resolution-summary">
                  <div className="flex items-center gap-2 mb-2">
                    {selected.resolution.action_type === "accept"
                      ? <CheckCircle size={14} className="text-green-400" />
                      : <RotateCcw size={14} className="text-amber-400" />}
                    <span className={`text-xs font-bold uppercase tracking-widest ${selected.resolution.action_type === "accept" ? "text-green-400" : "text-amber-400"}`}>
                      {selected.resolution.action_type === "accept" ? "Recovery Accepted" : "Manual Override Applied"}
                    </span>
                  </div>
                  {selected.resolution.selected_option && (
                    <div className="text-xs text-foreground font-semibold">{selected.resolution.selected_option.title}</div>
                  )}
                  {selected.resolution.override_reason && (
                    <div className="text-xs text-muted-foreground mt-1">Reason: {selected.resolution.override_reason}</div>
                  )}
                  {selected.resolution.override_description && (
                    <div className="text-xs text-muted-foreground mt-0.5">{selected.resolution.override_description}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    By {selected.resolution.resolved_by} · {new Date(selected.resolution.resolved_at).toLocaleString("en-IN")}
                  </div>
                  {selected.resolution.roster_changes?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/40 space-y-1">
                      <div className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Roster Changes Applied</div>
                      {selected.resolution.roster_changes.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ArrowRight size={9} className="text-green-400 shrink-0" />
                          {c.description}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showNew && <NewDisruptionModal onClose={() => setShowNew(false)} onCreated={(evt) => { setEvents(e => [evt, ...e]); setSelected(evt); fetchAll(); }} />}
      {overrideOption !== null && <OverrideModal option={overrideOption} onConfirm={handleOverrideConfirm} onClose={() => setOverrideOption(null)} />}
      {resolving && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center">
          <div className="bg-card border border-border rounded-sm px-6 py-4 flex items-center gap-3">
            <RefreshCw size={16} className="text-primary animate-spin" />
            <span className="text-sm text-foreground">Applying resolution and updating roster...</span>
          </div>
        </div>
      )}
    </div>
  );
}
